import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DynamicFormComponent } from 'src/app/components/dynamic-form/dynamic-form.component';
import { IconModule } from 'src/app/icon/icon.module';
import { MaterialModule } from 'src/app/material.module';
import { ToastService } from 'src/app/services/toast.service';
import { PATTERN } from 'src/app/shared/constants';
import { FormControlType } from 'src/app/shared/enums';
import { IFormConfig } from 'src/app/shared/interfaces/IForm';
import { ISelection } from 'src/app/shared/interfaces/ISelect';
import { CountryService } from 'src/app/services/country.service';
import { UserService } from 'src/app/services/user.service';
import { CompanyService } from 'src/app/services/company.service';

@Component({
  selector: 'app-user-modal',
  imports: [MaterialModule, IconModule, TranslateModule, DynamicFormComponent],
  templateUrl: './user-modal.component.html',
  styleUrl: './user-modal.component.scss'
})
export class UserModalComponent implements OnInit {
  @ViewChild('systemUserForm', { static: true }) systemUserForm!: DynamicFormComponent;
  userForm: FormGroup;
  userFormConfig: IFormConfig;
  selectionData: ISelection[];
  spinLoader: boolean;
  title: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,
    private dialogRef: MatDialogRef<UserModalComponent>,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private toastService: ToastService,
    private countryService: CountryService,
    private companyService: CompanyService,
    private userService: UserService) { }

  ngOnInit() {
    this.title = this.data.userData ? 'edit' : 'add';
    this.initForm();
  }

  initForm() {
    const companyId = this.data.companyId ? this.data.companyId : this.data.userData?.companyId;
    this.userForm = this.formBuilder.group({
      id: [this.data.userData?.id ?? 0],
      name: [this.data.userData?.name, [Validators.required, Validators.maxLength(100)]],
      email: [this.data.userData?.email, [Validators.required, Validators.pattern(PATTERN.EMAIL)]],
      phone: [this.data.userData?.phone, [Validators.pattern(PATTERN.PHONE)]],
      address: [this.data.userData?.address, [Validators.maxLength(200)]],
      zipCode: [this.data.userData?.zipCode, [Validators.maxLength(20)]],
      city: [this.data.userData?.city, [Validators.maxLength(150)]],
      countryId: [this.data.userData?.countryId],
      companyId: [companyId],
      isActive: [companyId ? null : (this.data.userData?.isActive ?? true)],
      hasPefDatabaseAccess: [this.data.userData?.hasPefDatabaseAccess ?? false]
    });

    this.userFormConfig = {
      id: 'userForm',
      styles: {
        bodyClass: 'p-24',
        footerClass: 'd-flex justify-content-end p-24 b-t-1'
      },
      actions: [
        {
          isStroked: true,
          text: 'cancel',
          onClick: () => this.closeDialog()
        },
        {
          text: 'save',
          type: 'submit'
        }
      ],
      items: [
        {
          key: 'name',
          label: 'full name',
          layoutConfig: [{ column: 6 }],
          controlType: FormControlType.INPUT,
          validator: [{ errorType: 'required', errorMessage: 'full name required' }]
        },
        {
          key: 'email',
          label: 'email',
          layoutConfig: [{ column: 6 }],
          controlType: FormControlType.INPUT,
          validator: [
            { errorType: 'required', errorMessage: 'email required' },
            { errorType: 'pattern', errorMessage: 'email invalid' }
          ]
        },
        {
          key: 'phone',
          label: 'phone',
          layoutConfig: [{ column: 6 }],
          controlType: FormControlType.INPUT,
          validator: [
            { errorType: 'pattern', errorMessage: 'phone invalid' }
          ]
        },
        {
          key: 'zipCode',
          label: 'zip code',
          layoutConfig: [{ column: 6 }],
          controlType: FormControlType.INPUT
        },
        {
          key: 'city',
          label: 'city',
          layoutConfig: [{ column: 6 }],
          controlType: FormControlType.INPUT
        },
        {
          key: 'address',
          label: 'address',
          layoutConfig: [{ column: 6 }],
          controlType: FormControlType.INPUT
        },
        {
          key: 'countryId',
          label: 'country',
          layoutConfig: [{ column: 6 }],
          controlType: FormControlType.SELECT,
          fetchDataAPI: () => this.countryService.getCountries({ pageSize: 1000 }),
          selectionConfig: {
            title: 'name',
            key: 'id'
          }
        },
        {
          key: 'companyId',
          label: 'company',
          layoutConfig: [{ column: 6 }],
          controlType: FormControlType.SELECT,
          fetchDataAPI: () => this.companyService.getCompanies({ pageSize: 1000 }),
          selectionConfig: {
            title: 'name',
            key: 'id'
          },
          hidden: () => !!this.data.companyId
        },
        {
          key: 'hasPefDatabaseAccess',
          label: 'access to the pef database',
          controlType: FormControlType.CHECKBOX,
          layoutConfig: [{ column: 3 }],
          itemClass: 'd-flex align-items-center'
        },
        {
          key: 'isActive',
          label: 'active',
          controlType: FormControlType.SWITCH,
          layoutConfig: [{ column: 3 }],
          itemClass: 'd-flex align-items-center',
          hidden: () => !!(this.data.companyId || this.userForm?.get('companyId')?.value)
        }
      ]
    }
  }

  addOrUpdateUserAdmin = () => {
    if (!this.userForm.valid) {
      this.userForm.markAllAsTouched();
    } else {
      this.spinLoader = true;
      const redirectUrl = `${window.location.origin}/reset-password`;
      const formValue = this.userForm.value;
      const companyId = this.data.companyId ?? formValue.companyId;
      const payload = {
        email: this.data.userData?.email,
        ...formValue,
        companyId,
        isActive: companyId ? null : formValue.isActive
      };
      this.userService.addOrUpdateUser(payload, redirectUrl).subscribe({
        next: (res) => {
          const toastMsg = this.data.userData ? 'update successfully' : 'add successfully';
          this.toastService.success(this.translateService.instant(toastMsg))
          this.dialogRef.close(res);
        },
        error: ({ error }) => {
          this.spinLoader = false;
          this.systemUserForm.ValidatorError = error;
        }
      });
    }
  }

  closeDialog = (result?: any) => {
    this.dialogRef.close(result);
  }
}
